<template>
  <v-form id="exemption-form" v-model="valid" ref="form" lazy-validation>
    <v-container fluid>
      <v-layout column wrap class="pb-4">
        <v-flex xs12>
          <v-select
            v-if="exemptionMode === 'INDIVIDUAL'"
            attach
            id="createExemption-student"
            name="studentId"
            :label="`${ $t('student.student') }`"
            autocomplete
            required
            :filter="x => x"
            :items="students"
            :disabled="!!exemptionId"
            :rules="requiredRule"
            :search-input.sync="searchStudents"
            @change="studentChange"
            v-model="selectedStudent">
          </v-select>
          <div v-if="exemptionMode === 'GROUP'">
            <p>{{ $t('you_select') }}
              <strong>{{selectedGroup.name}}</strong>
              ({{ $t('watchlist_results', {count: selectedGroup.results}, selectedGroup.results) }})
            </p>
          </div>
        </v-flex>
        <v-flex xs12>
          {{$t('exemption.form.reason_prompt')}}
          <v-select
            id="createExemption-reason"
            attach
            v-bind:items="exemptionReasonsSelect"
            v-model="exemptionReason"
            content-class="exemption-reason-content"
            :label="$t('exemption.form.reason_prompt_placeholder')"
            single-line
            required
            :rules="requiredRule"/>
        </v-flex>
        <v-flex xs12>
          {{$t('exemption.form.date_prompt')}}
          <date-range-picker :minStart="null" :now="null" :dateRange="dateRange"
                             id="createExemption-date"
                             @updated="updateDateRange" :start-disabled="startDateDisabled"
                             :vertical="true" :attach="true"
                             :end-disabled="endDateDisabled" :min-end="null"/>
          <span v-if="dateRangeInvalid" class="caption text-red">{{$t('invalid_date_range')}}</span>
        </v-flex>
      </v-layout>
      *{{$t('required_fields')}}
      <v-flex v-if="exemptionError">
        <span :style="{color: $vuetify.theme.error}">{{$t(exemptionError.type, exemptionError.data)}}</span>
      </v-flex>
      <v-layout row justify-center class="mt-4">
        <v-btn id="createExemption-cancel" @click="cancel" :disabled="sending">
          {{$t('cancel')}}
        </v-btn>
        <v-btn @click="createClicked"
               id="createExemption-create"
               :disabled="sending"
               color="primary"
               v-if="sendingShow">
          <span v-if="exemptionId">{{$t('update')}}</span>
          <span v-else>{{$t('create')}}</span>
        </v-btn>
      </v-layout>
    </v-container>
  </v-form>
</template>

<script>
  import moment from 'moment-es6'
  import DateRangePicker from '../date/DateRangePicker'
  import { mapActions, mapMutations, mapState } from 'vuex'
  import {
    SET_DRAWER_STATE,
    SET_EXEMPTION_END_DATE,
    SET_EXEMPTION_END_DISABLED,
    SET_EXEMPTION_REASON,
    SET_EXEMPTION_START_DATE,
    SET_EXEMPTION_START_DISABLED,
    SET_INTERVENTION_STUDENT,
    SET_INTERVENTION_STUDENTS
  } from '../../store/mutation-types'
  import {
    CREATE_EXEMPTION, CREATE_GROUP_EXEMPTION,
    GET_INTERVENTION_STUDENTS_LIST,
    UPDATE_EXEMPTION
  } from '../../store/action-types'
  import VueScrollTo from 'vue-scrollto'

  export default {
    name: 'exemption-form',
    data () {
      return {
        valid: false,
        searchStudents: null,
        requiredRule: [v => !!v || this.$t('field_required')],
        now: moment().format('YYYY-MM-DD'),
        sendingShow: true,
        dateRangeInvalid: false
      }
    },
    watch: {
      searchStudents (val) {
        val && this.queryStudents(val)
      }
    },
    methods: {
      ...mapActions({
        queryStudents: GET_INTERVENTION_STUDENTS_LIST,
        createExemption: CREATE_EXEMPTION,
        createGroupExemption: CREATE_GROUP_EXEMPTION,
        updateExemption: UPDATE_EXEMPTION
      }),
      ...mapMutations({
        setExemptionStartDateDisabled: SET_EXEMPTION_START_DISABLED,
        setExemptionEndDateDisabled: SET_EXEMPTION_END_DISABLED
      }),
      studentChange (data) {
        /* data is different types
             number - a valid student
             event - an unknown student name.
           The issue was unknown names appeared blank without a validation error.
         */
        if (data.srcElement) {
          this.selectedStudent = ''
        }
      },
      updateDateRange (newDates) {
        this.startDate = newDates.startDate
        this.endDate = newDates.endDate
      },
      cancel () {
        this.dateRangeInvalid = false
        this.$store.commit(SET_DRAWER_STATE, false)
      },
      createClicked () {
        let diff = moment(this.dateRange.endDate).diff(moment(this.dateRange.startDate), 'days')
        if (diff >= 0) {
          this.dateRangeInvalid = false
          if (this.$refs.form.validate()) {
            let payload = {
              studentId: this.selectedStudent,
              startDate: this.dateRange.startDate,
              endDate: this.dateRange.endDate,
              exemptionReasonId: this.exemptionReason,
              exemptionId: this.exemptionId
            }
            if (this.exemptionMode === 'GROUP') {
              payload.myGroupId = this.selectedGroup.id
              this.createGroupExemption(payload)
            } else {
              this.exemptionId ? this.updateExemption(payload) : this.createExemption(payload)
            }
          } else {
            // Makes the button wiggle around due to the animation associated with it
            this.sendingShow = false
            this.$nextTick(() => {
              this.sendingShow = true
            })
            // Scroll to the first element with a validation error
            for (let i = 0; i < this.$refs.form.getInputs().length; i++) {
              if (this.$refs.form.getInputs()[i].hasError) {
                let options = {
                  container: '#right-drawer',
                  easing: 'ease-in-out',
                  offset: -64,
                  cancelable: false
                }
                VueScrollTo.scrollTo(this.$refs.form.getInputs()[i].$el, 500, options)
                break
              }
            }
          }
        } else {
          this.dateRangeInvalid = true
        }
      },
      resetForm () {
        this.$nextTick(() => {
          this.$refs.form.inputs.forEach((input) => {
            this.dateRangeInvalid = false
            input.shouldValidate = false
            input.isBooted = false
            input.hasFocused = false
          })
        })
      }
    },
    computed: {
      ...mapState({
        exemptionReasons: state => state.exemptionModule.exemptionReasons,
        exemptionReason: state => state.exemptionModule.currentExemptionReason,
        startDateDisabled: state => state.exemptionModule.startDateDisabled,
        endDateDisabled: state => state.exemptionModule.endDateDisabled,
        exemptionId: state => state.exemptionModule.exemptionId,
        sending: state => state.exemptionModule.sending,
        exemptionError: state => state.exemptionModule.errorMessage,
        exemptionMode: state => state.intervention.interventionMode,
        selectedGroup: state => state.groups.selectedGroup
      }),
      exemptionReasonsSelect () {
        if (this.exemptionReasons) {
          return this.exemptionReasons.map(item => {
            return {
              text: this.$t(item.text),
              value: item.value
            }
          })
        } else {
          return []
        }
      },
      students: {
        get () {
          return this.$store.state.intervention.students
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_STUDENTS, value)
        }
      },
      exemptionReason: {
        get () {
          return this.$store.state.exemptionModule.currentExemptionReason
        },
        set (value) {
          this.$store.commit(SET_EXEMPTION_REASON, value)
        }
      },
      selectedStudent: {
        get () {
          return this.$store.state.intervention.selectedStudent
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_STUDENT, value)
        }
      },
      startDate: {
        get () {
          return this.$store.state.exemptionModule.startDate
        },
        set (value) {
          this.$store.commit(SET_EXEMPTION_START_DATE, value)
        }
      },
      endDate: {
        get () {
          return this.$store.state.exemptionModule.endDate
        },
        set (value) {
          this.$store.commit(SET_EXEMPTION_END_DATE, value)
        }
      },
      dateRange () {
        return {
          startDate: this.startDate,
          endDate: this.endDate
        }
      }
    },
    components: {
      DateRangePicker
    }
  }
</script>

<style scoped>
  #exemption-form {
    padding-bottom: 150px;
  }
  /deep/ .exemption-reason-content .list__tile__title {
    height: auto !important;
    min-height: 24px !important;
    word-wrap: break-word !important;
    white-space: inherit !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/exemption/ExemptionForm.vue